import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import UpperBarIn from "../UpperBar/UpperBarIn";
import './AuthCards.css';
import coverImage from "../../../images/couvImage.jpg";
import profileImage from "../../../images/profile.jpg";
import silverBadge from "../../../images/silverBadge.png";
import goldBadge from "../../../images/goldBadge.png";
import modulesIcon from "../../../images/moduleIcon.png";
import agendaIcon from "../../../images/agendaIcon.png";
import tasksIcon from "../../../images/tasksIcon.png";
import formationIcon from "../../../images/formationsIcon.png";
import competencesIcon from "../../../images/formationsIcon.png";
import performanceIcon from "../../../images/formationsIcon.png";
import recrutementIcon from "../../../images/formationsIcon.png";
import handbookIcon from "../../../images/formationsIcon.png";
import selfcareIcon from "../../../images/formationsIcon.png";
import { useNavigate } from 'react-router-dom';
import { setSelectedRole } from "../../../redux/actions/roleActions";
import {API_BASE_URL} from "../../../../api";
import {updateMyProfileRequest, uploadUserPhotoRequest} from "../../../redux/actions/userActions";
import { fetchPendingFields } from "../../../redux/actions/changeRequestActions";
import { getUserSoldesRequest } from "../../../redux/actions/leaveBalanceActions";
import { saveNavigationSelectionForModule } from "../../../utils/navigationSelectionStorage";

const getUserInitials = (user) => {
    const fullNameParts = (user?.fullName || "").trim().split(/\s+/).filter(Boolean);
    const firstName = user?.firstName || user?.prenom || fullNameParts[0] || "";
    const lastName = user?.lastName || user?.nom || (fullNameParts.length > 1 ? fullNameParts[fullNameParts.length - 1] : "") || "";
    return `${lastName.charAt(0)}${firstName.charAt(0)}`.toUpperCase() || "U";
};

const normalizeAppPath = (path) => {
    if (!path || typeof path !== "string") return "";
    const trimmed = path.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const getRolePath = (role) => {
    const roleName = `${role?.name || ''} ${role?.code || ''}`.toUpperCase();
    const compactRoleName = roleName.replace(/\s+/g, '');
    if (Number(role?.id) === 54 || compactRoleName.includes('RESPONSABLEONBOARDING')) return '/recrutement/onboarding/configuration';
    if (Number(role?.id) === 53 || compactRoleName.includes('NRONBOARDING')) return '/collaborateur/onboarding';
    const explicitPath = normalizeAppPath(role?.routePath || role?.modulePath || role?.module_path);
    if (explicitPath) return explicitPath;
    return '';
};
const isNewRecruitRole = (role) => Number(role?.id) === 53;
const getPreferredModuleRole = (roles = [], moduleId) => {
    const moduleRoles = (roles || []).filter(
        (role) => String(role.moduleId) === String(moduleId)
    );

    return moduleRoles.find((role) => role?.isDefault)
        || moduleRoles.find((role) => isNewRecruitRole(role) && getRolePath(role))
        || moduleRoles.find((role) => !isNewRecruitRole(role) && getRolePath(role))
        || moduleRoles.find((role) => getRolePath(role))
        || moduleRoles[0];
};

const formatLeaveDays = (value) => {
    const days = Number(value || 0);
    const formatted = Number.isInteger(days) ? String(days) : days.toFixed(2).replace(/\.?0+$/, "");
    return `${formatted} jour${days > 1 ? 's' : ''}`;
};

const getMainLeaveBalance = (soldes = []) => {
    if (!Array.isArray(soldes) || soldes.length === 0) return null;

    const annualKeywords = ["annuel", "annual", "conge annuel", "congé annuel", "ca", "cp", "payé", "paye"];
    const annualSolde = soldes.find((solde) => {
        const label = String(solde?.absenceTypeNom || solde?.absenceTypeName || "").toLowerCase().trim();
        const code = String(solde?.absenceTypeCode || "").toLowerCase().trim();
        return annualKeywords.some((keyword) => label.includes(keyword) || code.includes(keyword));
    });

    return annualSolde || soldes[0];
};

const getModuleImageUrl = (picture) =>
    picture ? `${API_BASE_URL}/api/modules/images/${picture}` : "";

/* ─── ALL AGENDA EVENTS ──────────────────────────────────────── */
const ALL_AGENDA_EVENTS = [
    { date: '2025-10-03', time: '09:00', title: "Kick-off projet Q4", type: 'meeting' },
    { date: '2025-10-08', time: '11:00', title: 'Revue des candidatures', type: 'interview' },
    { date: '2025-10-14', time: '14:00', title: 'Formation Excel avancé', type: 'training' },
    { date: '2025-10-17', time: '10:30', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2025-10-21', time: '15:00', title: 'Entretien annuel', type: 'interview' },
    { date: '2025-10-28', time: '09:00', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2025-10-28', time: '14:30', title: 'Entretien RH', type: 'interview' },
    { date: '2025-10-29', time: '10:00', title: 'Formation Leadership', type: 'training' },
    { date: '2025-10-30', time: '15:00', title: 'Point mensuel', type: 'meeting' },
    { date: '2026-03-04', time: '09:30', title: 'Réunion stratégie Q1', type: 'meeting' },
    { date: '2026-03-04', time: '14:00', title: 'Entretien candidat Dev', type: 'interview' },
    { date: '2026-03-10', time: '10:00', title: 'Formation Gestion de projet', type: 'training' },
    { date: '2026-03-12', time: '11:30', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2026-03-17', time: '09:00', title: 'Point RH mensuel', type: 'meeting' },
    { date: '2026-03-19', time: '15:00', title: 'Entretien bilan mi-année', type: 'interview' },
    { date: '2026-03-24', time: '14:30', title: 'Atelier onboarding', type: 'training' },
    { date: '2026-03-27', time: '10:00', title: 'Revue de performance', type: 'meeting' },
    { date: '2026-03-31', time: '16:00', title: 'Clôture bilan Q1', type: 'meeting' },
];

/* ─── ALL TASKS DATA ─────────────────────────────────────────── */
const INITIAL_TASKS = [
    {
        id: 1,
        title: 'Validation de recrutement',
        status: 'todo',
        detail: 'Valider les 3 candidatures reçues pour le poste de chef de projet et transmettre la décision aux RH.',
        module: 'Recrutement',
    },
    {
        id: 2,
        title: 'Validation de demande de formation',
        status: 'inprogress',
        detail: 'Examiner la demande de formation Leadership soumise par M. Dupont et approuver ou refuser dans Talentsoft.',
        module: 'Formation',
    },
    {
        id: 3,
        title: 'Compléter l\'évaluation annuelle',
        status: 'todo',
        detail: 'Remplir le formulaire d\'évaluation annuelle pour les 5 membres de l\'équipe avant le 31 octobre.',
        module: 'Performance',
    },
    {
        id: 4,
        title: 'Mise à jour du handbook RH',
        status: 'done',
        detail: 'Intégrer les nouvelles politiques de télétravail dans le handbook RH v2025.',
        module: 'Handbook',
    },
];

const STATUS_CONFIG = {
    todo:       { label: 'À faire',  color: '#f59e0b', bg: '#fef3c7', dot: '#f59e0b' },
    inprogress: { label: 'En cours', color: '#3b82f6', bg: '#dbeafe', dot: '#3b82f6' },
    done:       { label: 'Terminé',  color: '#10b981', bg: '#d1fae5', dot: '#10b981' },
};

/* ─── MINI CALENDAR ─────────────────────────────────────────── */
const DAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
    'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function MiniCalendar({ onOpenAgenda }) {
    const today = new Date();
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const eventDaysSet = new Set(
        ALL_AGENDA_EVENTS
            .filter(ev => {
                const d = new Date(ev.date);
                return d.getFullYear() === viewYear && d.getMonth() === viewMonth;
            })
            .map(ev => new Date(ev.date).getDate())
    );

    const prev = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };
    const next = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <div className="mini-calendar">
            <div className="cal-header">
                <button className="cal-nav" onClick={prev}>‹</button>
                <span
                    className="cal-month-label cal-month-clickable"
                    onClick={() => onOpenAgenda(viewYear, viewMonth)}
                    title="Voir les événements du mois"
                >
                    {MONTHS_FR[viewMonth].slice(0, 4)}. {viewYear}
                </span>
                <button className="cal-nav" onClick={next}>›</button>
            </div>
            <div className="cal-grid">
                {DAYS_FR.map((d, i) => (
                    <div key={i} className="cal-day-name">{d}</div>
                ))}
                {cells.map((day, i) => {
                    const isToday =
                        day === today.getDate() &&
                        viewMonth === today.getMonth() &&
                        viewYear === today.getFullYear();
                    const hasEvent = eventDaysSet.has(day);
                    return (
                        <div
                            key={i}
                            className={[
                                'cal-day',
                                !day ? 'empty' : '',
                                isToday ? 'today' : '',
                                hasEvent && day ? 'has-event' : ''
                            ].join(' ').trim()}
                            onClick={() => day && hasEvent && onOpenAgenda(viewYear, viewMonth)}
                        >
                            {day || ''}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── MODULES MODAL ──────────────────────────────────────────── */
const ModulesModal = ({ isOpen, onClose, onModuleClick, userModules }) => {
    const { t } = useTranslation();
    const fallbackModules = [
        { name: 'Formation',     icon: formationIcon },
        { name: 'Compétences',   icon: competencesIcon },
        { name: 'Performance',   icon: performanceIcon },
        { name: 'Recrutement',   icon: recrutementIcon },
        { name: 'Handbook',      icon: handbookIcon },
        { name: 'Selfcare Admin',icon: selfcareIcon },
    ];
    const displayModules = userModules && userModules.length
        ? userModules.map(m => ({
            ...m,
            icon: getModuleImageUrl(m.picture || m.modulePicture || m.moduleImage) || formationIcon,
            hasModuleImage: Boolean(m.picture || m.modulePicture || m.moduleImage),
        }))
        : fallbackModules;

    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><img src={modulesIcon} alt="" className="header-icon" />{t('Mes modules')}</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modules-grid">
                    {displayModules.map((module, index) => (
                        <div key={index} className="module-card clickable"
                             onClick={() => { onClose(); onModuleClick(module); }}>
                            <img
                                src={module.icon}
                                alt={module.name}
                                className={`module-icon-img ${module.hasModuleImage ? "module-icon-img--photo" : ""}`}
                            />
                            <span className="module-name">{t(module.name)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─── AGENDA MODAL ───────────────────────────────────────────── */
const AgendaModal = ({ isOpen, onClose, year, month }) => {
    const { t } = useTranslation();
    const monthEvents = ALL_AGENDA_EVENTS.filter(ev => {
        const d = new Date(ev.date);
        return d.getFullYear() === year && d.getMonth() === month;
    });
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><img src={agendaIcon} alt="" className="header-icon" />
                        {t('Événements du mois')} — {MONTHS_FR[month]} {year}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="agenda-content">
                    {monthEvents.length === 0 ? (
                        <div className="agenda-empty">{t('Aucun événement ce mois-ci')}</div>
                    ) : monthEvents.map((event, index) => (
                        <div key={index} className="agenda-event">
                            <div className="event-date">
                                <span className="event-day">{new Date(event.date).getDate()}</span>
                                <span className="event-month">
                                    {new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}
                                </span>
                            </div>
                            <div className="event-details">
                                <div className="event-time">{event.time}</div>
                                <div className="event-title">{t(event.title)}</div>
                                <span className={`event-type ${event.type}`}>{t(event.type)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─── TASK DETAIL MODAL ──────────────────────────────────────── */
const TaskDetailModal = ({ task, onClose, onStatusChange }) => {
    const { t } = useTranslation();
    if (!task) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content task-detail-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <img src={tasksIcon} alt="" className="header-icon" />
                        {t('Détail de la tâche')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="task-detail-body">
                    <div className="task-detail-module-tag">{task.module}</div>
                    <h3 className="task-detail-title">{task.title}</h3>
                    <p className="task-detail-desc">{task.detail}</p>
                    <div className="task-detail-status-section">
                        <span className="task-detail-status-label">{t('Statut')}</span>
                        <div className="task-detail-status-pills">
                            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                                <button
                                    key={key}
                                    className={`status-pill ${task.status === key ? 'active' : ''}`}
                                    style={task.status === key
                                        ? { background: val.bg, color: val.color, borderColor: val.color }
                                        : {}}
                                    onClick={() => onStatusChange(task.id, key)}
                                >
                                    <span className="status-pill-dot" style={{ background: val.dot }} />
                                    {val.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── EDIT INFO MODAL ────────────────────────────────────────── */
const INITIAL_EDIT_FORM = {
    firstName: 'Emma',
    lastName: 'SABRI',
    cin: '',
    birthDate: '',
    email: 'bluescratch358@gmail.com',
    phone: '',
    phoneUrgence: '',
    adresse: '',
    ville: '',
    codePostal: '',
    statutMarital: '',
    nombreEnfants: '0',
    personneUrgence: '',
    rib: '',
    iban: '',
    typeContrat: '',
    category: '',
    // password fields
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
};

const EditInfoModal = ({ isOpen, onClose, onSave, user, pendingFields = [], isPending, getPendingData }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ ...INITIAL_EDIT_FORM });
    const [showPasswords, setShowPasswords] = useState({ current: false, next: false, confirm: false });
    const [passwordError, setPasswordError] = useState('');

    const FieldLabel = ({ label, fieldKey }) => {
        const pendingData = getPendingData(fieldKey);
        
        return (
            <label className="edit-field-label">
                {label}
                {pendingData && (
                    <span className="pending-indicator-wrap">
                        <span className="pending-indicator" title={`Demandé : ${pendingData.newValue}\nActuel : ${pendingData.oldValue || 'Vide'}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                        </span>
                        <span className="pending-values-hint">
                            (Attente: {pendingData.newValue})
                        </span>
                    </span>
                )}
            </label>
        );
    };

    React.useEffect(() => {
        if (isOpen && user) {
            setForm({
                firstName:       user.firstName       || '',
                lastName:        user.lastName        || '',
                cin:             user.cin             || '',
                birthDate:       user.dateOfBirth     || '',
                email:           user.email           || '',
                phone:           user.phone           || '',
                phoneUrgence:    user.phoneUrgence    || '',
                adresse:         user.adresse         || '',
                ville:           user.city            || '',
                codePostal:      user.codePostal      || '',
                statutMarital:   user.statutMarital   || '',
                nombreEnfants:   user.nombreEnfants   != null ? String(user.nombreEnfants) : '0',
                personneUrgence: user.personneUrgence || '',
                rib:             user.rib             || '',
                iban:            user.iban            || '',
                typeContrat:     user.typeContrat     || '',
                category:        user.category        || '',
                currentPassword: '',
                newPassword:     '',
                confirmPassword: '',
            });
            setPasswordError('');
        }
    }, [isOpen, user]);

    if (!isOpen) return null;

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = () => {
        if (form.newPassword || form.confirmPassword || form.currentPassword) {
            if (!form.currentPassword) { setPasswordError('Veuillez saisir le mot de passe actuel.'); return; }
            if (form.newPassword.length < 6) { setPasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères.'); return; }
            if (form.newPassword !== form.confirmPassword) { setPasswordError('Les mots de passe ne correspondent pas.'); return; }
        }
        setPasswordError('');
        onSave(form);
        onClose();
    };

    const togglePass = (key) => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));

    const EyeIcon = ({ open }) => open ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
    ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
        </svg>
    );

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-info-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--blue)' }}>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        {t('Modifier mes informations')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="edit-info-body">

                    {/* ── Identité ── */}
                    <div className="edit-section-title">Identité</div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="Prénom" fieldKey="firstName" />
                            <input 
                                className={`edit-field-input ${isPending('firstName') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.firstName} 
                                onChange={e => set('firstName', e.target.value)} 
                                placeholder="Prénom"
                                disabled={isPending('firstName')}
                            />
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Nom" fieldKey="lastName" />
                            <input 
                                className={`edit-field-input ${isPending('lastName') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.lastName} 
                                onChange={e => set('lastName', e.target.value)} 
                                placeholder="Nom"
                                disabled={isPending('lastName')}
                            />
                        </div>
                    </div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="CIN / Passeport" fieldKey="cin" />
                            <input 
                                className={`edit-field-input ${isPending('cin') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.cin} 
                                onChange={e => set('cin', e.target.value)} 
                                placeholder="CIN / Passeport"
                                disabled={isPending('cin')}
                            />
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Date de naissance" fieldKey="birthDate" />
                            <input 
                                className={`edit-field-input ${isPending('birthDate') ? 'field-pending' : ''}`} 
                                type="date" 
                                value={form.birthDate} 
                                onChange={e => set('birthDate', e.target.value)}
                                disabled={isPending('birthDate')}
                            />
                        </div>
                    </div>

                    {/* ── Contact ── */}
                    <div className="edit-section-title">Contact</div>
                    <div className="edit-field-group">
                        <FieldLabel label="Email" fieldKey="email" />
                        <input className="edit-field-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" />
                    </div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="Téléphone" fieldKey="phone" />
                            <input 
                                className={`edit-field-input ${isPending('phone') ? 'field-pending' : ''}`} 
                                type="tel" 
                                value={form.phone} 
                                onChange={e => set('phone', e.target.value)} 
                                placeholder="Téléphone"
                                disabled={isPending('phone')}
                            />
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Téléphone d'urgence" fieldKey="phoneUrgence" />
                            <input 
                                className={`edit-field-input ${isPending('phoneUrgence') ? 'field-pending' : ''}`} 
                                type="tel" 
                                value={form.phoneUrgence} 
                                onChange={e => set('phoneUrgence', e.target.value)} 
                                placeholder="Téléphone d'urgence"
                                disabled={isPending('phoneUrgence')}
                            />
                        </div>
                    </div>
                    <div className="edit-field-group">
                        <FieldLabel label="Adresse" fieldKey="adresse" />
                        <input 
                            className={`edit-field-input ${isPending('adresse') ? 'field-pending' : ''}`} 
                            type="text" 
                            value={form.adresse} 
                            onChange={e => set('adresse', e.target.value)} 
                            placeholder="Adresse"
                            disabled={isPending('adresse')}
                        />
                    </div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="Ville" fieldKey="ville" />
                            <input 
                                className={`edit-field-input ${isPending('ville') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.ville} 
                                onChange={e => set('ville', e.target.value)} 
                                placeholder="Ville"
                                disabled={isPending('ville')}
                            />
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Code postal" fieldKey="codePostal" />
                            <input 
                                className={`edit-field-input ${isPending('codePostal') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.codePostal} 
                                onChange={e => set('codePostal', e.target.value)} 
                                placeholder="Code postal"
                                disabled={isPending('codePostal')}
                            />
                        </div>
                    </div>

                    {/* ── Situation personnelle ── */}
                    <div className="edit-section-title">Situation personnelle</div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="Statut marital" fieldKey="statutMarital" />
                            <select 
                                className={`edit-field-input edit-field-select ${isPending('statutMarital') ? 'field-pending' : ''}`} 
                                value={form.statutMarital} 
                                onChange={e => set('statutMarital', e.target.value)}
                                disabled={isPending('statutMarital')}
                            >
                                <option value="">-- Sélectionner --</option>
                                <option value="celibataire">Célibataire</option>
                                <option value="marie">Marié(e)</option>
                                <option value="divorce">Divorcé(e)</option>
                                <option value="veuf">Veuf/Veuve</option>
                                <option value="pacse">Pacsé(e)</option>
                            </select>
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Nombre d'enfants" fieldKey="nombreEnfants" />
                            <input 
                                className={`edit-field-input ${isPending('nombreEnfants') ? 'field-pending' : ''}`} 
                                type="number" 
                                min="0" 
                                value={form.nombreEnfants} 
                                onChange={e => set('nombreEnfants', e.target.value)}
                                disabled={isPending('nombreEnfants')}
                            />
                        </div>
                    </div>
                    <div className="edit-field-group">
                        <FieldLabel label="Personne à contacter en cas d'urgence" fieldKey="personneUrgence" />
                        <input 
                            className={`edit-field-input ${isPending('personneUrgence') ? 'field-pending' : ''}`} 
                            type="text" 
                            value={form.personneUrgence} 
                            onChange={e => set('personneUrgence', e.target.value)} 
                            placeholder="Nom & prénom + téléphone"
                            disabled={isPending('personneUrgence')}
                        />
                    </div>

                    {/* ── Informations bancaires ── */}
                    <div className="edit-section-title">Informations bancaires</div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="RIB" fieldKey="rib" />
                            <input 
                                className={`edit-field-input ${isPending('rib') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.rib} 
                                onChange={e => set('rib', e.target.value)} 
                                placeholder="RIB"
                                disabled={isPending('rib')}
                            />
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="IBAN" fieldKey="iban" />
                            <input 
                                className={`edit-field-input ${isPending('iban') ? 'field-pending' : ''}`} 
                                type="text" 
                                value={form.iban} 
                                onChange={e => set('iban', e.target.value)} 
                                placeholder="IBAN"
                                disabled={isPending('iban')}
                            />
                        </div>
                    </div>

                    {/* ── Informations contractuelles ── */}
                    <div className="edit-section-title">Informations contractuelles</div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <FieldLabel label="Type de contrat" fieldKey="typeContrat" />
                            <select 
                                className={`edit-field-input edit-field-select ${isPending('typeContrat') ? 'field-pending' : ''}`} 
                                value={form.typeContrat} 
                                onChange={e => set('typeContrat', e.target.value)}
                                disabled={isPending('typeContrat')}
                            >
                                <option value="">-- Sélectionner --</option>
                                <option value="CDI">CDI</option>
                                <option value="CDD">CDD</option>
                            </select>
                        </div>
                        <div className="edit-field-group">
                            <FieldLabel label="Catégorie" fieldKey="category" />
                            <select 
                                className={`edit-field-input edit-field-select ${isPending('category') ? 'field-pending' : ''}`} 
                                value={form.category} 
                                onChange={e => set('category', e.target.value)}
                                disabled={isPending('category')}
                            >
                                <option value="">-- Sélectionner --</option>
                                <option value="POSTE_FIXE">Poste fixe</option>
                                <option value="MENSUEL">Mensuel</option>
                                <option value="HORAIRE">Horaire</option>
                            </select>
                        </div>
                    </div>

                    {/* ── Mot de passe ── */}
                    <div className="edit-section-title">Mot de passe</div>
                    <div className="edit-field-group">
                        <label className="edit-field-label">Mot de passe actuel</label>
                        <div className="edit-pass-wrap">
                            <input className="edit-field-input" type={showPasswords.current ? 'text' : 'password'} value={form.currentPassword} onChange={e => set('currentPassword', e.target.value)} placeholder="••••••••" />
                            <button type="button" className="edit-pass-toggle" onClick={() => togglePass('current')}><EyeIcon open={showPasswords.current} /></button>
                        </div>
                    </div>
                    <div className="edit-row-2">
                        <div className="edit-field-group">
                            <label className="edit-field-label">Nouveau mot de passe</label>
                            <div className="edit-pass-wrap">
                                <input className="edit-field-input" type={showPasswords.next ? 'text' : 'password'} value={form.newPassword} onChange={e => set('newPassword', e.target.value)} placeholder="••••••••" />
                                <button type="button" className="edit-pass-toggle" onClick={() => togglePass('next')}><EyeIcon open={showPasswords.next} /></button>
                            </div>
                        </div>
                        <div className="edit-field-group">
                            <label className="edit-field-label">Confirmer le mot de passe</label>
                            <div className="edit-pass-wrap">
                                <input className="edit-field-input" type={showPasswords.confirm ? 'text' : 'password'} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••" />
                                <button type="button" className="edit-pass-toggle" onClick={() => togglePass('confirm')}><EyeIcon open={showPasswords.confirm} /></button>
                            </div>
                        </div>
                    </div>
                    {passwordError && (
                        <div className="edit-password-error">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            {passwordError}
                        </div>
                    )}

                    <div className="edit-info-actions">
                        <button className="edit-cancel-btn" onClick={onClose}>{t('Annuler')}</button>
                        <button className="edit-save-btn" onClick={handleSave}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            {t('Enregistrer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── EDIT PHOTO MODAL ───────────────────────────────────────── */
const EditPhotoModal = ({ isOpen, onClose, currentPhoto, onSave, title, subtitle, isPending }) => {
    const { t } = useTranslation();
    const [preview, setPreview] = useState(null);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const loading = useSelector(state => state.user.loading);

    React.useEffect(() => {
        if (isOpen) setPreview(null);
        if (isOpen) setPreview(null);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setSelectedFile(file);                               // ← File object pour dispatch
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result); // ← base64 pour la preview visuelle
        reader.readAsDataURL(file);
    };


    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        handleFile(file);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-photo-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--blue)', marginRight: 6 }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        {title || t('Modifier la photo')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="edit-photo-body">
                    {isPending && (
                        <div className="pending-photo-alert">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span>Une photo est déjà en attente de traitement</span>
                        </div>
                    )}
                    {subtitle && <p className="edit-photo-subtitle">{subtitle}</p>}

                    {/* Preview */}
                    <div className="edit-photo-preview-wrap">
                        <img
                            src={preview || currentPhoto}
                            alt="Preview"
                            className="edit-photo-preview"
                        />
                        {preview && (
                            <div className="edit-photo-preview-badge">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Nouvelle photo
                            </div>
                        )}
                    </div>

                    {/* Drop zone */}
                    <div
                        className={`edit-photo-dropzone ${dragging ? 'dragging' : ''} ${isPending ? 'disabled' : ''}`}
                        onDragOver={e => { if (isPending) return; e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => { if (isPending) return; handleDrop(e); }}
                        onClick={() => !isPending && fileInputRef.current?.click()}
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="16 16 12 12 8 16"/>
                            <line x1="12" y1="12" x2="12" y2="21"/>
                            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                        </svg>
                        <span className="dropzone-main-text">
                            {dragging ? 'Relâchez pour importer' : 'Glissez une image ici'}
                        </span>
                        <span className="dropzone-sub-text">ou <strong>cliquez pour parcourir</strong> · JPG, PNG, WebP</span>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={e => handleFile(e.target.files[0])}
                        />
                    </div>

                    <div className="edit-info-actions">
                        <button className="edit-cancel-btn" onClick={onClose}>{t('Annuler')}</button>
                        <button
                            className="edit-save-btn"
                            disabled={!selectedFile || loading || isPending}
                            onClick={() => { if (selectedFile && !isPending) { onSave(selectedFile); onClose(); } }}
                        >
                            {loading ? 'Envoi...' : t('Enregistrer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── TASKS SECTION (inline col 3) ──────────────────────────── */
const TasksSection = ({ onTaskClick }) => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState(INITIAL_TASKS);
    const [filter, setFilter] = useState('all');

    const toggleDone = (id) => {
        setTasks(prev => prev.map(tk =>
            tk.id === id
                ? { ...tk, status: tk.status === 'done' ? 'todo' : 'done' }
                : tk
        ));
    };

    const filters = [
        { key: 'all',        label: 'Toutes' },
        { key: 'todo',       label: 'À faire' },
        { key: 'inprogress', label: 'En cours' },
        { key: 'done',       label: 'Terminées' },
    ];

    const filtered = filter === 'all' ? tasks : tasks.filter(tk => tk.status === filter);
    const doneCount = tasks.filter(tk => tk.status === 'done').length;

    return (
        <div className="sidebar-section tasks-section">
            <div className="section-header">
                <h3>
                    <img src={tasksIcon} alt="" className="section-icon" />
                    {t('Mes tâches')}
                </h3>
                <div className="tasks-header-right">
                    <span className="tasks-progress-text">{doneCount}/{tasks.length}</span>
                    <span className="badge-count">{tasks.filter(tk => tk.status !== 'done').length}</span>
                </div>
            </div>
            <div className="tasks-progress-bar">
                <div
                    className="tasks-progress-fill"
                    style={{ width: `${Math.round((doneCount / tasks.length) * 100)}%` }}
                />
            </div>
            <div className="tasks-filter-row">
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`task-filter-pill ${filter === f.key ? 'active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <div className="tasks-list">
                {filtered.length === 0 && (
                    <div className="tasks-empty">Aucune tâche dans cette catégorie</div>
                )}
                {filtered.map(task => {
                    const cfg = STATUS_CONFIG[task.status];
                    return (
                        <div
                            key={task.id}
                            className={`task-row ${task.status === 'done' ? 'task-done' : ''}`}
                        >
                            <button
                                className={`task-checkbox ${task.status === 'done' ? 'checked' : ''}`}
                                onClick={() => toggleDone(task.id)}
                                title={task.status === 'done' ? 'Marquer non terminé' : 'Marquer terminé'}
                            >
                                {task.status === 'done' && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                            <div className="task-row-content" onClick={() => onTaskClick(task)}>
                                <span className="task-row-title">{task.title}</span>
                                <span className="task-row-status" style={{ color: cfg.color, background: cfg.bg }}>
                                    <span className="task-status-dot" style={{ background: cfg.dot }} />
                                    {cfg.label}
                                </span>
                            </div>
                            <button className="task-detail-btn" onClick={() => onTaskClick(task)} title="Voir le détail">›</button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── EDIT ICON SVG ──────────────────────────────────────────── */
const EditIcon = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
const AuthCards = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.auth?.user);
    const userSoldes = useSelector(state => state.leaveBalance?.userSoldes || []);
    const loading = useSelector(state => state.user.loading);
    const [profileImageFailed, setProfileImageFailed] = useState(false);


    const today = new Date();
    const [activeTab, setActiveTab] = useState('module');
    const pendingFields = useSelector(state => state.userChangeRequests.pendingFields);
    const [isModulesModalOpen, setIsModulesModalOpen] = useState(false);
    const [availableModules, setAvailableModules] = useState([]);
    const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);
    const [agendaModalYear, setAgendaModalYear] = useState(today.getFullYear());
    const [agendaModalMonth, setAgendaModalMonth] = useState(today.getMonth());
    const [selectedTask, setSelectedTask] = useState(null);

    // ── Edit modals state
    const [isEditInfoOpen, setIsEditInfoOpen] = useState(false);

    React.useEffect(() => {
        if (isEditInfoOpen && user?.id) {
            dispatch(fetchPendingFields(user.id));
        }
    }, [isEditInfoOpen, user?.id, dispatch]);

    React.useEffect(() => {
        const userId = user?.id || sessionStorage.getItem("uid");
        const companyId = user?.companyId || sessionStorage.getItem("companyId");
        if (userId) {
            dispatch(getUserSoldesRequest({ userId, companyId }));
        }
    }, [user?.id, user?.companyId, dispatch]);

    React.useEffect(() => {
        setProfileImageFailed(false);
    }, [user?.profilePictureUrl]);

    React.useEffect(() => {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        const companyId = user?.companyId || sessionStorage.getItem('selectedCompanyId');
        const uid = user?.id || sessionStorage.getItem('uid');

        if (!token || !companyId) return;

        fetch(`${API_BASE_URL}/api/modules`, {
            headers: {
                Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                uid: uid ? String(uid) : '',
                'X-Tenant-Id': String(companyId),
            },
        })
            .then((response) => (response.ok ? response.json() : []))
            .then((data) => setAvailableModules(Array.isArray(data) ? data : []))
            .catch(() => setAvailableModules([]));
    }, [user?.companyId, user?.id]);
    const [isEditProfilePhotoOpen, setIsEditProfilePhotoOpen] = useState(false);
    const [isEditCoverOpen, setIsEditCoverOpen] = useState(false);

    // Mapping between UI field keys and Backend field keys
    const fieldMapping = {
        firstName: "firstName",
        lastName: "lastName",
        cin: "cin",
        birthDate: "dateOfBirth",
        phone: "phone",
        phoneUrgence: "phoneUrgence",
        adresse: "adresse",
        ville: "city",
        codePostal: "codePostal",
        statutMarital: "statutMarital",
        nombreEnfants: "nombreEnfants",
        personneUrgence: "personneUrgence",
        rib: "rib",
        iban: "iban",
        typeContrat: "typeContrat",
        category: "category"
    };

    const isPending = (fieldKey) => {
        const backendKey = fieldMapping[fieldKey] || fieldKey;
        return pendingFields.some(req => req.fieldKey === backendKey);
    };

    const getPendingData = (fieldKey) => {
        const backendKey = fieldMapping[fieldKey] || fieldKey;
        return pendingFields.find(req => req.fieldKey === backendKey);
    };

    const congeBalance = React.useMemo(() => {
        const mainSolde = getMainLeaveBalance(userSoldes);
        return mainSolde ? formatLeaveDays(mainSolde.soldeRestant) : formatLeaveDays(0);
    }, [userSoldes]);

    const profilePhotoSrc = user && user.profilePictureUrl
        ? `${API_BASE_URL}/api/users/images/profile/${user.profilePictureUrl}`
        : "";
    const hasProfilePhoto = !!profilePhotoSrc && !profileImageFailed;
    const userInitials = getUserInitials(user);

    console.log("useruser",user)
    const bannerSrc = user && user.bannerPictureUrl
        ? `${API_BASE_URL}/api/users/images/banner/${user.bannerPictureUrl}`
        : coverImage; // fallback image locale

    const openAgenda = (year, month) => {
        setAgendaModalYear(year);
        setAgendaModalMonth(month);
        setIsAgendaModalOpen(true);
    };

    const handleSaveProfile = (formData) => {
        if (!user || !user.id) return;
        dispatch(updateMyProfileRequest(user.id, {
            firstName:        formData.firstName        || null,
            lastName:         formData.lastName         || null,
            phone:            formData.phone            || null,
            dateOfBirth:      formData.birthDate        || null,
            city:             formData.ville            || null,
            bio:              null,
            cin:              formData.cin              || null,
            phoneUrgence:     formData.phoneUrgence     || null,
            adresse:          formData.adresse          || null,
            codePostal:       formData.codePostal       || null,
            statutMarital:    formData.statutMarital    || null,
            nombreEnfants:    formData.nombreEnfants    ? parseInt(formData.nombreEnfants) : null,
            personneUrgence:  formData.personneUrgence  || null,
            rib:              formData.rib              || null,
            iban:             formData.iban             || null,
            typeContrat:      formData.typeContrat      || null,
            category:         formData.category         || null,
            // Mot de passe — ignoré côté backend si currentPassword est vide
            currentPassword:  formData.currentPassword  || null,
            newPassword:      formData.newPassword      || null,
            confirmPassword:  formData.confirmPassword  || null,
        }));
        setIsEditInfoOpen(false);
    };
    // Upload photo de profil
    const handleSaveProfilePhoto = (file) => {
        if (!user || !user.id) return;
        dispatch(uploadUserPhotoRequest(user.id, file, 'profile'));
    };

    // Upload bannière
    const handleSaveBanner = (file) => {
        if (!user || !user.id) return;
        dispatch(uploadUserPhotoRequest(user.id, file, 'banner'));
    };

    const MODULE_ROUTES = {
        "Selfcare Admin": "/collaborateur/dashboard",
        "Skills": "/skills/dashboard/collaborateur",
        "Compétences": "/skills/dashboard/collaborateur",
        "Formation": "/formation/dashboard/collaborateur",
        "Performance": "/performance/dashboard/collaborateur",
        "Recrutement": "/recrutement/dashboard/collaborateur",
        "Handbook": "/handbook/dashboard/collaborateur",
    };

    const getModulePath = (m) => {
        const byCode = {
            SELFCARE: "/collaborateur/dashboard",
            SKILLS: "/skills/dashboard/collaborateur",
            FORMATION: "/formation/dashboard/collaborateur",
            PERF: "/performance/dashboard/collaborateur",
            RECRUTEMENT: "/recrutement/dashboard/collaborateur",
            HANDBOOK: "/handbook/dashboard/collaborateur",
        };
        return normalizeAppPath(m?.routePath || m?.modulePath || m?.module_path) ||
            byCode[(m?.code || '').trim()] ||
            MODULE_ROUTES[(m?.name || '').trim()] ||
            "/collaborateur/dashboard";
    };

    const handleModuleClick = (module) => {
        saveNavigationSelectionForModule(module, user?.id);
        const targetRole = getPreferredModuleRole(user?.roles, module.id);
        if (targetRole) dispatch(setSelectedRole({ ...targetRole, userId: user?.id }));
        navigate(getRolePath(targetRole) || getModulePath(module));
    };

    const getUserModules = (u) => {
        if (!u?.roles || !Array.isArray(u.roles)) return [];
        const map = new Map();
        const modulesById = new Map((availableModules || []).map((module) => [String(module.id), module]));
        u.roles.forEach(role => {
            if (role.moduleId && role.moduleName) {
                const fullModule = modulesById.get(String(role.moduleId)) || {};
                map.set(role.moduleId, {
                    id: role.moduleId,
                    name: fullModule.name || role.moduleName,
                    code: role.code,
                    roleName: role.name,
                    routePath: fullModule.routePath || role.routePath || role.modulePath || role.module_path,
                    picture: fullModule.picture || role.modulePicture || role.moduleImage || role.picture || "",
                });
            }
        });
        return Array.from(map.values());
    };

    const userModules = getUserModules(user);
    const shownModules = userModules.length
        ? userModules.slice(0, 2)
        : [
            { id: 'f1', name: 'Formation', code: 'FORMATION' },
            { id: 'f2', name: 'Performance', code: 'PERF' },
        ];

    const badges = [
        { type: 'silver', img: silverBadge },
        { type: 'silver', img: silverBadge },
        { type: 'gold',   img: goldBadge },
    ];

    const navigationByModule = [
        { name: 'Formation',   value: 80 },
        { name: 'Recrutement', value: 90 },
        { name: 'Performance', value: 65 },
        { name: 'Compétences', value: 85 },
        { name: 'Selfcare',    value: 50 },
    ];

    const navigationByMonth = [
        { month: 'Jan', Formation: 12, Compétences: 10, Recrutement: 9,  Performance: 7,  Handbook: 6,  'Selfcare Admin': 4 },
        { month: 'Fév', Formation: 15, Compétences: 12, Recrutement: 11, Performance: 8,  Handbook: 7,  'Selfcare Admin': 5 },
        { month: 'Mar', Formation: 18, Compétences: 14, Recrutement: 13, Performance: 6,  Handbook: 6,  'Selfcare Admin': 5 },
        { month: 'Avr', Formation: 14, Compétences: 15, Recrutement: 15, Performance: 10, Handbook: 5,  'Selfcare Admin': 4 },
        { month: 'Mai', Formation: 19, Compétences: 13, Recrutement: 10, Performance: 9,  Handbook: 6,  'Selfcare Admin': 6 },
        { month: 'Juin', Formation: 21, Compétences: 17, Recrutement: 14, Performance: 10, Handbook: 9, 'Selfcare Admin': 8 },
        { month: 'Juil', Formation: 18, Compétences: 14, Recrutement: 13, Performance: 10, Handbook: 7, 'Selfcare Admin': 6 },
        { month: 'Août', Formation: 20, Compétences: 16, Recrutement: 15, Performance: 12, Handbook: 10,'Selfcare Admin': 9 },
        { month: 'Sep',  Formation: 17, Compétences: 13, Recrutement: 14, Performance: 9,  Handbook: 7,  'Selfcare Admin': 6 },
    ];

    const thisMonthCount = ALL_AGENDA_EVENTS.filter(ev => {
        const d = new Date(ev.date);
        return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
    }).length;

    const renderBarLabel = ({ x, y, width, value }) => (
        <text x={x + width / 2} y={y - 5} fill="#1e3a8a"
              textAnchor="middle" fontSize="12" fontWeight="600">{value}%</text>
    );

    const firstName = user?.firstName || user?.fullName?.split(' ')[0] || 'Emma';
    const lastName  = user?.lastName  || 'SABRI';

    return (
        <div className="auth-cards-page">
            <UpperBarIn />
            <div className="auth-cards-content">

                {/* ── BANNER ── */}
                <div className="banner-wrapper">
                    <img src={bannerSrc} alt="Cover" className="cover-image" />

                    <button
                        className="banner-edit-btn"
                        onClick={() => setIsEditCoverOpen(true)}
                        title="Modifier la photo de couverture"
                    >
                        <EditIcon size={13} color="white" />
                        <span>Modifier la bannière</span>
                    </button>

                    <div className="profile-image-wrap" onClick={() => setIsEditProfilePhotoOpen(true)}>
                        <div className="profile-image-ring">
                            {hasProfilePhoto ? (
                                <img
                                    src={profilePhotoSrc}
                                    alt="Profile"
                                    className="profile-image"
                                    onError={() => setProfileImageFailed(true)}
                                />
                            ) : (
                                <span className="profile-image-initials">{userInitials}</span>
                            )}
                        </div>
                        <div className="profile-photo-edit-overlay" title="Modifier la photo de profil">
                            <EditIcon size={15} color="white" />
                        </div>
                    </div>

                    <div className="banner-content">
                        <div className="profile-info">
                            <h1>{t('Bonjour,')} <span className="highlight-name">{firstName}</span> !</h1>
                            <p className="profile-role">{user && user.jobName || 'Directrice commerciale'}</p>
                        </div>
                        <div className="last-connection">
                            {t('Dernière connexion')} · {user?.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('Première connexion')}
                        </div>
                    </div>
                </div>

                {/* ── 3-COLUMN GRID ── */}
                <div className="columns-container">

                    {/* COL 1 */}
                    <div className="column column-1">
                        <div className="col1-header">
                            <div className="emp-name">{firstName} {lastName}</div>
                            <button
                                className="col1-edit-btn"
                                onClick={() => setIsEditInfoOpen(true)}
                                title="Modifier les informations"
                            >
                                <EditIcon size={13} />
                            </button>
                        </div>
                        {[
                            { label: t('Poste'),          value: user?.jobName || t('Non défini') },
                            { label: t('Ancienneté'),     value: user?.seniority || t('Non définie') },
                            { label: t('Direction'),      value: user?.entityName || t('Non définie') },
                            { label: t('Manager'),        value: user?.managerFullName || t('Non défini') },
                            { label: t('Type de contrat'),value: user?.typeContrat || t('Non défini') },
                            // { label: t('Catégorie'),      value: user?.category || t('Non définie') },
                            { label: t('Solde de congé'), value: congeBalance },
                        ].map(({ label, value }) => (
                            <div key={label} className="info-row">
                                <span className="info-label">{label}</span>
                                <span className="info-value">{value}</span>
                            </div>
                        ))}
                        <div className="info-row">
                            <span className="info-label">{t('Badges reçus')}</span>
                            <div className="badges-row">
                                {badges.map((b, i) => (
                                    <img key={i} src={b.img} alt={b.type} className="badge-icon" />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* COL 2 */}
                    <div className="column column-2">
                        <div className="navigation-tabs">
                            <button className={`nav-tab ${activeTab === 'module' ? 'active' : ''}`} onClick={() => setActiveTab('module')}>
                                {t('Navigation par module')}
                            </button>
                            <button className={`nav-tab ${activeTab === 'month' ? 'active' : ''}`} onClick={() => setActiveTab('month')}>
                                {t('Navigation par mois')}
                            </button>
                        </div>
                        <div className="chart-container">
                            {activeTab === 'module' && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={navigationByModule} margin={{ top: 28, right: 20, left: -10, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" tick={{ fill: '#4b5563', fontSize: 11 }} />
                                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} domain={[0, 100]} />
                                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8 }} formatter={v => `${v}%`} />
                                        <defs>
                                            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#4b6cb7"/>
                                                <stop offset="100%" stopColor="#204385"/>
                                            </linearGradient>
                                        </defs>
                                        <Bar dataKey="value" fill="url(#barGradient)" radius={[8,8,0,0]} label={renderBarLabel} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                            {activeTab === 'month' && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={navigationByMonth} margin={{ top: 16, right: 20, left: -10, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="month" tick={{ fill: '#4b5563', fontSize: 11 }} />
                                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} domain={[0, 25]} />
                                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="line" />
                                        <Line type="monotone" dataKey="Formation"    stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        <Line type="monotone" dataKey="Compétences"  stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        <Line type="monotone" dataKey="Recrutement"  stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        <Line type="monotone" dataKey="Performance"  stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        <Line type="monotone" dataKey="Handbook"     stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        <Line type="monotone" dataKey="Selfcare Admin" stroke="#ec4899" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* COL 3 — Modules + Agenda */}
                    <div className="column column-3">
                        <div className="sidebar-section">
                            <div className="section-header">
                                <h3><img src={modulesIcon} alt="" className="section-icon" />{t('Mes modules')}</h3>
                            </div>
                            <div className="modules-chips">
                                {shownModules.map((m, i) => (
                                    <div key={i} className="module-chip" onClick={() => handleModuleClick(m)}>{t(m.name)}</div>
                                ))}
                            </div>
                            <button className="voir-plus-btn" onClick={() => setIsModulesModalOpen(true)}>
                                <span>{t('Tous les modules')}</span>
                                <span className="voir-plus-icon">+</span>
                            </button>
                        </div>

                        <div className="sidebar-section">
                            <div className="section-header">
                                <h3><img src={agendaIcon} alt="" className="section-icon" />{t('Mon Agenda')}</h3>
                                <span
                                    className="badge-count badge-count-clickable"
                                    onClick={() => openAgenda(today.getFullYear(), today.getMonth())}
                                    title={t('Voir les événements du mois')}
                                >
                                    {thisMonthCount}
                                </span>
                            </div>
                            <MiniCalendar onOpenAgenda={openAgenda} />
                        </div>
                    </div>

                    {/* COL 4 — Tasks */}
                    <div className="column column-4">
                        <TasksSection onTaskClick={setSelectedTask} />
                    </div>
                </div>
            </div>

            {/* ── MODALS ── */}
            <ModulesModal
                isOpen={isModulesModalOpen}
                onClose={() => setIsModulesModalOpen(false)}
                onModuleClick={handleModuleClick}
                userModules={userModules}
            />
            <AgendaModal
                isOpen={isAgendaModalOpen}
                onClose={() => setIsAgendaModalOpen(false)}
                year={agendaModalYear}
                month={agendaModalMonth}
            />
            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onStatusChange={(id, newStatus) => {
                        setSelectedTask(prev => prev && prev.id === id ? { ...prev, status: newStatus } : prev);
                    }}
                />
            )}

            {/* Edit user info modal */}

            <EditInfoModal
                isOpen={isEditInfoOpen}
                onClose={() => setIsEditInfoOpen(false)}
                onSave={handleSaveProfile}
                loading={loading}
                user={user}
                pendingFields={pendingFields}
                isPending={isPending}
                getPendingData={getPendingData}
            />

            {/* Edit profile photo modal */}
            <EditPhotoModal
                isOpen={isEditProfilePhotoOpen}
                onClose={() => setIsEditProfilePhotoOpen(false)}
                currentPhoto={profilePhotoSrc || profileImage}
                onSave={handleSaveProfilePhoto}
                title="Modifier la photo de profil"
                subtitle="Choisissez une photo carrée pour un meilleur rendu."
                loading={loading}
                isPending={isPending('profilePictureUrl')}
            />

            {/* Bannière — onSave reçoit un File object */}
            <EditPhotoModal
                isOpen={isEditCoverOpen}
                onClose={() => setIsEditCoverOpen(false)}
                currentPhoto={bannerSrc}
                onSave={handleSaveBanner}
                title="Modifier la photo de couverture"
                subtitle="Recommandé : image au format 16:9, minimum 1200×400px."
                loading={loading}
                isPending={isPending('bannerPictureUrl')}
            />
        </div>
    );
};

export default AuthCards;
